import React, { useContext, useEffect, useMemo } from "react";
import { withDevTools as createStoreWithDevTools } from 'botframework-webchat-core/lib/createStore';
import DruidWebchatContext, { DruidWebchatContextType, WebChatState } from "../WebChatContext";
import { ConnectionStatus, SenderActionAction } from 'botframework-directlinejs';
import { Subscription } from "rxjs/Subscription";
import { DruidCookies, WebChatOptions } from "../WebChatOptions";
import sendFiles from 'botframework-webchat-core/lib/actions/sendFiles';

import activityFromBot from 'botframework-webchat-core/src/definitions/activityFromBot';
import clearSuggestedActions from 'botframework-webchat-core/lib/actions/clearSuggestedActions';
import dismissNotification from 'botframework-webchat-core/lib/actions/dismissNotification';
import setLanguage from 'botframework-webchat-core/lib/actions/setLanguage';
import setNotification from 'botframework-webchat-core/lib/actions/setNotification';
import moment from "moment";
import { v4 as uuidv4 } from 'uuid';
import { getCookie, setCookie } from "../utils/cookiesUtils";
import { ensureHttpProtocol } from "../utils/urlUtils";
import pRetry from "p-retry";
import { doHeartBeat } from "../WebChatServices";
import { druidLanguageComparison, isNewActivity, mapConversationHistoryActivityToActivity, shouldEnter_sensitiveDataInputMode } from "../logics/druidBusinessLogic";
import { Observable, BehaviorSubject } from 'rxjs';

import { blockingInput_ignorableActivity } from "../logics/ignorableActivity";
import { DruidDocumentViewer } from "../components/DruidDocumentViewer";
import { determineDirection } from "../utils/localeUtils";


import { render } from "react-dom";
import { makeThumbnail } from "../utils/pictureUtils";

export const useCreateBFStore = ({ initialOptions }: { initialOptions: WebChatOptions }) => {

  const [druidWebChatState, druidWebChatDispatch, getState] = useContext<DruidWebchatContextType>(DruidWebchatContext);

  const debounceTypingOnOff = useMemo(() => {
    const behaviorSubject = new BehaviorSubject<boolean | string | null>(null);
    let typingOffTimeout: any = null;
    behaviorSubject.debounceTime(300).distinctUntilChanged().subscribe(showTyping => {
      if (showTyping !== null) {
        typingOffTimeout && clearTimeout(typingOffTimeout);
        if(showTyping === false) {
          typingOffTimeout = setTimeout(() => druidWebChatDispatch({ type: 'SET_SHOWTYPING', payload: showTyping }), 700);
        }
        druidWebChatDispatch({ type: 'SET_SHOWTYPING', payload: showTyping });
      }
    });
    return behaviorSubject;
  }, [druidWebChatDispatch]);

  useEffect(() => {
    return () => {
      debounceTypingOnOff && debounceTypingOnOff.complete();
    }
  }, [debounceTypingOnOff]);


  // should have a druidWebchatState.loaded == true dependency for reload/reset to work effective. used some conditional rendering to avoid issues.
  return useMemo(
    () => {
      const customDruidMiddleware = ({ dispatch, getState: getBFState }) => next => action => {
        druidWebChatDispatch(action); // forward to internal chat
        if (!action.payload?.activity?.channelData?.isActivityStatusUpdate) {
          // const currentState = getState();
          // initialOptions.webChatEventHandler && initialOptions.webChatEventHandler(action, currentState, getBFState());

          // if(currentState.mergedChatOptions.webChatEventHandler) {
          //   currentState.mergedChatOptions.webChatEventHandler(action, currentState, getBFState());
          // }
        } else { // if isActivityStatusUpdate and from 'bot' => return false and break the chain of actions
          const { activity: { from: { role = "" } = {} } } = action.payload || {};
          if (role === 'bot') {
            return false;
          }
        }
        return next(action);
      };
      const subscriptions: Subscription[] = [];
      let scrolledToTop = false;
      var cancelTypingTimeout: any = null;
      // initialOptions.webChatEventHandler && initialOptions.webChatEventHandler(action)
      const coreDruidMiddleware = ({ dispatch: bfDispatch, getState: getBFState }) => next => async action => {
        // const { mergedChatOptions, minimized } = druidWebChatState;
        const currentState = getState();
        const currentBFState = getBFState();
        if (action.type === 'DIRECT_LINE/INCOMING_ACTIVITY') {
          const { payload: { activity, activity: { channelData, locale: activityLocale } } } = action;
          if (!activity.channelData?.isActivityStatusUpdate) {
            if (activity.type === 'event') {
              const toLowerCaseName = ((activity.name as string) || "").toLowerCase();
              switch (toLowerCaseName) {
                case 'set-druid-cookies': {
                  druidWebChatDispatch({ type: 'SET_DRUID_COOKIES', payload: activity });
                  return;
                }
                case 'robotjsstartprocess': {
                  if (!!activity.value && isNewActivity(currentState, activity)) {
                    const params = JSON.parse(activity.value);
                    const isRobotJsV2 = !!params.EndpointUrl;
                    const handleException = (e: any): string => {
                      let errorMessage = e.Message || e.message;
                      let f = e.InnerException;
                      while (f != null) {
                        errorMessage += '/' + f.Message;
                        f = f.InnerException;
                      }
                      return errorMessage;
                    }

                    (async () => {
                      try {
                        const UiPathRobotPromise = await import(
                          /* webpackChunkName: "robotjs" */
                          '@uipath/robot'
                        );

                        const UiPathRobot = UiPathRobotPromise.UiPathRobot;

                        const robot = UiPathRobot.init();
                        const processes = await robot.getProcesses();

                        const storeState = getState();
                        const proc = processes.find(p => p.name.includes(params.ProcessName));
                        const jobId = uuidv4();

                        if (!proc) {
                          throw new Error(`UIPath Process <${params.ProcessName}> not found!`);
                        }

                        if (isRobotJsV2) {
                          // [robotjs v2]
                          const processParams = JSON.parse(params.ProcessParams);
                          const startProcessTimestamp = moment().utc().toISOString();
                          const jobPromise = proc.start(processParams);
                          jobPromise.onStatus(message => {
                            console.log(`${proc.name}: ${message}`);
                          });

                          await storeState.directlineInstance?.postActivity({
                            from: { id: storeState.mergedChatOptions.userId || "" },
                            name: 'RobotJsProcessStatus',
                            type: 'event',
                            value: { JobId: jobId, JobStatus: 'job.created' }
                          }).toPromise();

                          try {
                            const jobResults = await jobPromise;
                            const endProcessTimestamp = moment().utc().toISOString();
                            const results = {
                              Type: 'job.completed',
                              Token: params.EndpointSecret,
                              Job: {
                                // "Id": job.jobId,
                                Key: jobId,
                                // "State": "Successful",
                                StartTime: startProcessTimestamp,
                                EndTime: endProcessTimestamp,
                                Info: null,
                                OutputArguments: jobResults
                              }
                            };

                            await storeState.directlineInstance?.sendAjaxRequest({
                              url: params.EndpointUrl,
                              method: 'POST',
                              body: results,
                              headers: {
                                'Content-Type': 'application/json'
                              }
                            }).toPromise();
                          } catch (ex) {
                            const endProcessTimestamp = moment().utc().toISOString();
                            const errorResults = {
                              Type: 'job.faulted',
                              Token: params.EndpointSecret,
                              Job: {
                                Key: jobId,
                                StartTime: startProcessTimestamp,
                                EndTime: endProcessTimestamp,
                                Info: handleException(ex)
                              }
                            };

                            await storeState.directlineInstance?.sendAjaxRequest({
                              url: params.EndpointUrl,
                              method: 'POST',
                              body: errorResults,
                              headers: {
                                'Content-Type': 'application/json'
                              }
                            }).toPromise();
                          }

                        } else {
                          // [robotjs v1]

                          const job = proc.start(params);

                          const results = { $entityTypeName$: 'RobotJsOutput' };

                          const jobResults = await job;
                          Object.assign(results, jobResults);
                          storeState.directlineInstance?.postActivity({
                            from: { id: storeState.mergedChatOptions.userId || "" },
                            name: params.CallbackUtterance,
                            type: 'event',
                            value: results
                          }).subscribe(() => {
                            console.log(params.CallbackUtterance + ' sent');
                          });
                        }

                      } catch (e) {
                        // If anything goes wrong
                        console.log(`Exception: ${e}`);

                        const storeState = getState();

                        if (isRobotJsV2) {
                          await storeState.directlineInstance?.postActivity({
                            from: { id: storeState.mergedChatOptions.userId || "" },
                            name: 'RobotJsProcessStatus',
                            type: 'event',
                            value: { JobStatus: 'job.failed', Info: handleException(e) }
                          }).toPromise();

                        } else {
                          await storeState.directlineInstance?.postActivity({
                            from: { id: storeState.mergedChatOptions.userId || "" },
                            name: 'Exception',
                            type: 'event',
                            value: { $entityTypeName$: 'SystemError', Message: handleException(e), CorrelationId: params.CorrelationId }
                          }).toPromise();
                        }

                      }
                    })();
                  }
                  break;
                }

                case 'signdocument': {
                  try {
                    if (!!activity.value && (isNewActivity(currentState, activity) || localStorage.getItem('Druid_SignDocument_ActivityId') === activity.id)) {
                      const parsedJSON = JSON.parse(activity.value);
                      setTimeout(() => {
                        const determinedDir = determineDirection(getState().mergedChatOptions?.dir, getState().locale);
                        try {
                          localStorage.setItem('Druid_SignDocument_ActivityId', activity.id);
                          const rootElem = document.createElement('div');
                          rootElem.setAttribute('id', 'druidWebchatSignDocumentModal');
                          const drdWebchatRoot = document.body; // getElementById('druidWebchatRoot')
                          const newElem = drdWebchatRoot.appendChild(rootElem);
                          const { DocumentUrl: documentSrc, FileName: fileName, SignatureFieldText: signatureFieldText, SignatureScale: signatureScale = '1',
                           SignButtonName: signButtonName, CancelButtonName: cancelButtonName, SaveButtonName: saveButtonName, UploadButtonName: uploadButtonName, SignatureColorCode: signatureColorCode = "#000000" } = parsedJSON;
                          const reactElem = React.createElement(DruidDocumentViewer, { documentSrc, store: getState().store, determinedDir, fileName, signatureFieldText, signatureScale, signButtonName, cancelButtonName, saveButtonName, uploadButtonName, signatureColorCode}, null);
                          render(reactElem, newElem, () => {
                            document.querySelectorAll('#druid_jsDocumentViewer_v2 .documentViewer__container').item(0).scrollTop = 0;
                            const CustomModalWindow = document.getElementById('druid_jsDocumentViewer_v2');
                            CustomModalWindow && (CustomModalWindow.classList ? CustomModalWindow.classList.add('open') : CustomModalWindow.className += ' ' + 'open');
                          });
                        } catch (ex) {
                          getState().store.dispatch({
                            type: 'WEB_CHAT/SEND_EVENT',
                            payload: {
                              name: 'CancelUpload',
                              value: {}
                            }
                          });
                        }
                      }, 100);
                    }
                  } catch (ex) {
                    console.error(ex);
                  }
                  break;
                }
                case 'changetopreviousconversation':
                case 'changeconversation': { // should refactor -> don't use cookie.
                  try {
                    if (!!activity.value && isNewActivity(currentState, activity)) {
                      const parsedJSON = JSON.parse(activity.value);
                      if (parsedJSON.ConversationId !== currentState.directlineOptions?.conversationId) {
                        currentState.directlineInstance?.end(); // end previous connection
                        if (parsedJSON.DruidConversationToken) {
                          druidWebChatDispatch({
                            type: 'RESET',
                            withNewDruidConversationToken: parsedJSON.DruidConversationToken,
                            withNewAuthorizationDtoDetails: !!getState().mergedChatOptions?.authorizationResultDto && {
                              conversationId: parsedJSON.ConversationId,
                              token: parsedJSON.ConversationToken,
                              druidConversationToken: parsedJSON.DruidConversationToken,
                              watermark: '0'
                            },
                            withAutoExpand: true
                          });
                        }

                      }
                    }

                  } catch (ex) {
                    console.error(ex);
                  }

                  break;
                }

                case 'redirecttossopage': {
                  try {
                    if (!!activity.value && isNewActivity(currentState, activity)) {
                      try {
                        const parsedJSON = JSON.parse(activity.value);

                        const formatString = (str: string, ...val: string[]) => {
                          for (let index = 0; index < val.length; index++) {
                            str = str.replace(`{${index}}`, val[index]);
                          }
                          return str;
                        };

                        window.location.href = formatString(parsedJSON.RedirectUrl, window.location.href);
                      } catch (ex) {
                        console.error(`RedirectToSSoPage BCEvent failed with message: ${ex}`);
                      }
                    }
                  } catch (ex) {
                    console.error(ex);
                  }
                  break;
                }
                case 'redirecttopage': {
                  try {
                    if (!!activity.value && isNewActivity(currentState, activity)) {
                      try {
                        const parsedJSON = JSON.parse(activity.value);
                        parsedJSON.RedirectUrl = ensureHttpProtocol(parsedJSON.RedirectUrl);
                        if (!parsedJSON.InSameWindow) {
                          Object.assign(document.createElement('a'), { target: '_blank', href: parsedJSON.RedirectUrl }).click();
                        } else {
                          window.location.href = parsedJSON.RedirectUrl;
                        }
                      } catch (ex) {
                        console.error(`RedirectToPage BCEvent failed with message: ${ex}`);
                      }
                    }
                  } catch (ex) {
                    console.error(ex);
                  }
                  break;
                }

                case 'changedisplayname': {
                  if (!!activity.value) {
                    try {
                      const params = JSON.parse(activity.value);
                      if (params.BotDisplayName) {
                        druidWebChatDispatch({ type: 'SET_MERGEDCHATOPTIONS', payload: { UI: { activityStatusBotName: params.BotDisplayName } } });
                      }

                      if (params.UserDisplayName) {
                        druidWebChatDispatch({ type: 'SET_MERGEDCHATOPTIONS', payload: { UI: { activityStatusUserName: params.UserDisplayName } } });
                      }
                    } catch (ex) {

                    }
                  }
                  return;
                }

                case 'userauthenticated': {
                  if (!!activity.value) {
                    try {
                      const params = JSON.parse(activity.value);
                      if (params.FirstName) {
                        druidWebChatDispatch({ type: 'SET_MERGEDCHATOPTIONS', payload: { UI: { activityStatusUserName: params.FirstName } } });
                      }
                    } catch (ex) {

                    }
                  }
                  return;
                }

                case 'startliveness': {
                  try {
                    if (isNewActivity(currentState, activity)) {
                      (async () => {
                        await import(
                          /* webpackChunkName: "liveness" */
                          '@druidsoft/liveness/dist/build/static/js/druid_liveness.js'
                        );
                        if ((window as any).DruidLiveness) {
                          let params;
                          const parsedJSON = JSON.parse(activity.value);
                          console.log(parsedJSON);
                          if (parsedJSON.$entityTypeName$ === 'Liveness') {
                            params = {
                              intro_screen: parsedJSON.intro_screen || 1,
                              isWithLiveness: parsedJSON.isWithLiveness !== undefined ? parsedJSON.isWithLiveness : true,
                              isWithPicture: parsedJSON.isWithPicture === true,
                              videoRecordingMs: parsedJSON.videoRecordingMs !== undefined ? parsedJSON.videoRecordingMs : 5000,
                              UI: { chatBotAvatarUrl: parsedJSON.chatBotAvatarUrl, companyLogoUrl: parsedJSON.companyLogoUrl, modalFooterBgColor: parsedJSON.modalFooterBgColor },
                              language: parsedJSON.language !== undefined ? parsedJSON.language : "ro"
                            };
                          }

                          (window as any).DruidLiveness.init(params).subscribe(async o => {
                            if (o) {

                              const { styleOptions: { uploadThumbnailWidth, uploadThumbnailHeight, uploadThumbnailContentType, uploadThumbnailQuality } = {} } = getState().mergedChatOptions;
                             
                              const code = o.code;
                              const payload = o.payload;
                              // const eyesOpenedBlob = payload.eyesOpenedBlob;
                              // const videoRecordingBlob = payload.videoRecordingBlob;
                              // const isHuman = payload.isHuman;
                              // const arePermissionsGranted = payload.arePermissionsGranted;
                              // const hasError = payload.hasError;
                              // const instruction = payload.instruction;
                              // const instructionTime = payload.instructionTime;
                              const { eyesOpenedBlob, videoRecordingBlob, imageBlob, isHuman, arePermissionsGranted, hasError, instruction, instructionTime, isWithPicture, language } = payload;

                              const replace_bad_chars = function (text) {
                                return text.replace(/[<>"':;\\/|*!?]/g, "_")
                              };

                              switch (code) {
                                case 0: {
                                  console.log('A intrat pe code 0');
                                  const storeState = getState();

                                  storeState.store.dispatch({
                                    type: 'WEB_CHAT/SEND_EVENT',
                                    payload: {
                                      name: 'livenessselfie',
                                      value: { $entityTypeName$: 'Liveness' }
                                    }
                                  });

                                  if (eyesOpenedBlob) {
                                    console.log('S-a facut dispatch la img');
                                    const file = new File([eyesOpenedBlob], `liveness_selfie_${replace_bad_chars(activity.id)}_${(storeState.directlineInstance as any)?.conversationId}.png`, { type: eyesOpenedBlob.type }); // to change file name
                                    storeState.store.dispatch(sendFiles([{
                                      name: file.name,
                                      size: file.size,
                                      url: window.URL.createObjectURL(file),
                                      thumbnail: await makeThumbnail(
                                        file,
                                        uploadThumbnailWidth,
                                        uploadThumbnailHeight,
                                        uploadThumbnailContentType,
                                        uploadThumbnailQuality
                                      )
                                    }]));

                                    // this.store.dispatch(sendFiles([file] as any as FileList, this.props.user, this.props.locale));
                                  }
                                  break;
                                }
                                case 1: {
                                  const storeState = getState();
                                  storeState.store.dispatch({
                                    type: 'WEB_CHAT/SEND_EVENT',
                                    payload: {
                                      name: 'livenessresponse',
                                      value: {
                                        $entityTypeName$: 'Liveness',
                                        IsHuman: !!isHuman,
                                        arePermissionsGranted: !!arePermissionsGranted,
                                        hasError: !!hasError,
                                        instruction: instruction,
                                        instructionTime: instructionTime
                                      }
                                    }
                                  });
                                  // isWithPicture param is ignored. videoRecording has priority even though videoRecording param will not be sent if isWithPicture === true.
                                  if (videoRecordingBlob) {
                                    const extension = videoRecordingBlob.type === 'video/webm' ? 'webm' : 'mp4';
                                    const file = new File([videoRecordingBlob], `liveness_${replace_bad_chars(activity.id)}_${(storeState.directlineInstance as any)?.conversationId}.${extension}`, { type: videoRecordingBlob.type }); // to change file name
                                    storeState.store.dispatch(sendFiles([{
                                      name: file.name,
                                      size: file.size,
                                      url: window.URL.createObjectURL(file)
                                    }]));
                                  } else {
                                    if(imageBlob) {
                                      const file = new File([imageBlob], `liveness_image_${replace_bad_chars(activity.id)}_${(storeState.directlineInstance as any)?.conversationId}.png`, { type: imageBlob.type }); // to change file name
                                      storeState.store.dispatch(sendFiles([{
                                        name: file.name,
                                        size: file.size,
                                        url: window.URL.createObjectURL(file),
                                        thumbnail: await makeThumbnail(
                                          file,
                                          uploadThumbnailWidth,
                                          uploadThumbnailHeight,
                                          uploadThumbnailContentType,
                                          uploadThumbnailQuality
                                        )
                                      }]));
                                    }
                                  }
                                  break;
                                }
                                default:
                                  break;
                              }
                            }
                          });
                        }
                      })();
                    }
                  }
                  catch (ex) {
                    console.error(`StartLiveness failed with error message: ${ex}`);
                  }
                  break;
                }

                default:
                  break;
              }
            }

            if (activity.type === 'message') {
              if (activity.inputHint === 'blockingInput') { // blockinput
                // druidWebChatDispatch({ type: 'SET_MERGEDCHATOPTIONS', payload: { UI: { blockingInputDruidSendBox: true } } });
                if(currentState.mergedChatOptions?.UI?.accessibilityFeaturesEnabled && document.activeElement?.getAttribute('data-id') === 'webchat-sendbox-input') {
                  setTimeout(() =>  DruidWebchat_v2?.hooks?.focus("main"), 0);
                }
                druidWebChatDispatch({ type: 'SET_INPUT_BLOCKED', payload: true });
              } else {
                if (currentState.inputBlocked && !blockingInput_ignorableActivity(activity)) { // ignorableActivity used for inactivityMessage ->  don't unblock input
                  // druidWebChatDispatch({ type: 'SET_MERGEDCHATOPTIONS', payload: { UI: { blockingInputDruidSendBox: false } } });
                  druidWebChatDispatch({ type: 'SET_INPUT_BLOCKED', payload: false });
                }
              }

              if (activity.suggestedActions && activity.suggestedActions.actions?.length > 0 && activity.attachmentLayout) {
                druidWebChatDispatch({ type: 'SET_MERGEDCHATOPTIONS', payload: { styleOptions: { suggestedActionLayout: activity.attachmentLayout } } });
              }

              if (activityFromBot(activity) && activity.type === 'message') { // activity.type === 'message' is redundant
                if ((isNewActivity(currentState, activity)) && activityLocale && activityLocale !== currentBFState.language) { // change locale from message locale
                  bfDispatch(setLanguage(activityLocale));
                }

                const should_SENSITIVEDATAINPUT_MODE_be_enabled = shouldEnter_sensitiveDataInputMode(activity);
                // could be removed. added handler inside webchatReducer.
                // there was a problem, when DL is polling mode and two consecutive sensitive data inputs were sent (confirm password). this bit of code is processed before webchatReducer handlers -> SET_SENSITIVEDATAINPUT_MODE ended always false, because of the post_activity_fullfiled regarding the first step password.
                if (currentState.sensitiveDataInputMode !== should_SENSITIVEDATAINPUT_MODE_be_enabled) {
                  druidWebChatDispatch({ type: 'SET_SENSITIVEDATAINPUT_MODE', payload: should_SENSITIVEDATAINPUT_MODE_be_enabled });
                }

                if (isNewActivity(currentState, activity) && !!activity.channelData?.inactivityMessage) { // inactivityMessage
                  bfDispatch(setNotification({
                    id: 'inactivity_message_notification',
                    level: 'info',
                    message: activity.text,
                    alt: '',
                    data: {}
                  }));
                  return false;
                }

                if (currentState.mergedChatOptions?.notificationsEnabled && (!DruidWebchat_v2.isWindowFocused || currentState.minimized) && isNewActivity(currentState, activity)) { // new message notifications
                  druidWebChatDispatch({ type: 'SET_NEWMESSAGE', payload: true });
                  DruidWebchat_v2.notificationsSounds?.druidNewMessageNotification?.play().then(() => {
                  }).catch(() => {
                    setTimeout(() => {
                      try {
                        DruidWebchat_v2.notificationsSounds?.druidNewMessageNotification?.play();
                      } catch (ex) { }
                    }, 1000);
                  });
                }
              } else {
                if (currentBFState.suggestedActions?.length > 0) {// hide suggested actions if last message is not from bot
                  bfDispatch(clearSuggestedActions());
                }
              }

              //initially, when no message from the user is sent, autoScrollSnap won't work because of a BF limitation. this should fix the problem.
              if(!scrolledToTop && !!currentState.mergedChatOptions?.styleOptions?.autoScrollSnapOnPage && activityFromBot(activity) && activity.type === 'message' && currentBFState.activities && isNewActivity(currentState, activity)) {
                const hasUserActivities = currentBFState.activities.some(act => act?.type === 'message' && !activityFromBot(act));
                if(!hasUserActivities) {
                  scrolledToTop = true;
                  DruidWebchat_v2.hooks.scrollTo({ scrollTop: 0}, 'auto');
                }
              }
            }
          }
        }
        if (action.type === 'DIRECT_LINE/QUEUE_INCOMING_ACTIVITY') {
          const { payload: { activity, activity: { channelData } } } = action;
          // used for different avatar rendering
          if(!!activity?.channelData?.routedMessageId && activity?.from?.role !== 'user') {
              activity.from.role = 'helpdesk-agent';
          }
          if (!activity.channelData?.isActivityStatusUpdate) {
            if (activity.type === 'typing') { // show hide typing override
              if (!channelData || channelData.isHideTyping !== true) {
                debounceTypingOnOff.next((currentState.mergedChatOptions?.UI?.showTypingName && activity.from?.name) || true);
                // druidWebChatDispatch({type: 'SET_SHOWTYPING', payload: true});
              } else {
                debounceTypingOnOff.next(false);

                // druidWebChatDispatch({type: 'SET_SHOWTYPING', payload: false});
              }
            }

            if(activity.type === 'message' && (!activityFromBot(activity) || !!activity.channelData?.clientActivityID)) {
              activity.textFormat = 'plain';
            }
          }
        }
        if (action.type === 'DIRECT_LINE/CONNECT_FULFILLED') {
          subscriptions.push(action.payload.directLine.webServiceStatus$
            .subscribe((serviceStatus) => {
              const currentBFState = getBFState();
              if (currentBFState.connectivityStatus !== "uninitialized") {
                druidWebChatDispatch({ type: 'SERVICESTATUS_CHANGE', payload: serviceStatus });
              }
            }
            ));
          subscriptions.push(action.payload.directLine.connectionStatus$
            .subscribe((connStatus) => {
              if (connStatus === ConnectionStatus.ExpiredToken || connStatus === ConnectionStatus.FailedToConnect) {
                druidWebChatDispatch({ type: 'RESET' });
              }
            }
            ));
        }

        if (action.type === 'DIRECT_LINE/DISCONNECT') {
        }

        if (action.type === 'DIRECT_LINE/DISCONNECT_FULFILLED') {

        }

        if (action.type === 'WEB_CHAT/SET_SUGGESTED_ACTIONS') { // set suggestedActions layout based on attachmentLayout (uncommented)
          // if(action.payload.suggestedActions && action.payload.suggestedActions.length > 0 && action.payload.attachmentLayout) {
          //   druidWebChatDispatch({ type: 'SET_MERGEDCHATOPTIONS', payload: { styleOptions: { suggestedActionLayout: action.payload.attachmentLayout  }} });
          // }
          if (currentState.sensitiveDataInputMode && action.payload.suggestedActions && !action.payload.suggestedActions.length) { // if sensitiveDataInputMode is on, don't hide suggestedActions
            return false;
          }
        }

        if (action.type === 'WEB_CHAT/SEND_MESSAGE') {
          if(currentState.selectedActivityForReply) {
            if(action.payload) {
              action.payload.channelData ||= {};
              action.payload.channelData.replyActivityId = currentState.selectedActivityForReply?.id;
              const routedMessageId = currentState.selectedActivityForReply?.channelData.routedMessageId;
              if(routedMessageId) {
                action.payload.channelData.sourceMessageId = routedMessageId;
              }

            }
            druidWebChatDispatch({ type: 'SET_SELECTEDACTIVITY_FOR_REPLY', payload: { activity: null }})
          }
          if (currentState.sensitiveDataInputMode && !action.payload._druidInternal_suggestedAction_cancel_button && !action.payload._druidInternal_passThrough_sensitiveDataInputMode) {
            if (action.payload.method === 'keyboard') {
              druidWebChatDispatch({ type: 'INTERNAL/PROCCESS_SENSITIVE_MESSAGE', payload: { originalSendMessage: action } });
            }

            return false;
          }
          if (currentBFState.notifications && currentBFState.notifications['inactivity_message_notification']) {
            bfDispatch(dismissNotification('inactivity_message_notification'));
          }

          if (currentState.mergedChatOptions.heartBeatUrl) { // heartbeat
            try {
              const heartBeatResult = await pRetry(async () => {
                const heartBeatCallResult = await doHeartBeat({
                  baseUrl: currentState.mergedChatOptions.heartBeatUrl || ""
                });

                return heartBeatCallResult.status;
              }, {
                retries: 0,
                minTimeout: 1000,
                maxTimeout: 2000
              }).catch(() => {
                return 500;
              });

              if (heartBeatResult >= 500) {
                document.location.href = '/'; // go to home of website
              }

            } catch (ex) {
              console.error(ex);
            }
          }
        }
        if (action.type === 'DIRECT_LINE/UPDATE_CONNECTION_STATUS') {
          //   dispatch({
          //     type: 'WEB_CHAT/SEND_EVENT',
          //     payload: {
          //       name: 'webchat/join',
          //       value: {
          //         language: window.navigator.language
          //       }
          //     }
          //   });
          // this.webServiceStatusSubscription = botConnection.webServiceStatus$.subscribe(serviceStatus => this.store.dispatch<ChatActions>({ type: 'ServiceStatus_Change', serviceStatus }));
          // subscriptions.push(action.payload.directLine.webServiceStatus$.subscribe((serviceStatus) => {
          //   // debugger;
          //   druidWebChatDispatch({ type: 'SERVICESTATUS_CHANGE', payload: serviceStatus });
          // }
          // ));
        }

        if (action.type === 'WEB_CHAT/SET_LANGUAGE' && action.payload?.language) { // triggered from setLanguage dispatch above
          // druidWebChatDispatch({
          //       type: 'SET_LOCALE',
          //       payload: action.payload.language
          //     });
          if (!druidLanguageComparison(currentState.locale, action.payload.language)) {
            druidWebChatDispatch({
              type: 'SET_SELECTEDLANGUAGE',
              payload: action.payload.language,
              skipPostEvent: true
            });
          }
        }

        if (action.type === 'WEB_CHAT/SET_SEND_BOX') {
          const { text: sendBoxValue } = action.payload;

          if (!!cancelTypingTimeout) {
            clearTimeout(cancelTypingTimeout);
          }
          if (sendBoxValue) { // to ignore '' value when SUBMIT_SEND_BOX. we only cancel previous typingOn action. it will be handled by 'WEB_CHAT/SUBMIT_SEND_BOX'
            cancelTypingTimeout = setTimeout(() => {
              currentState.directlineInstance?.postSenderAction(SenderActionAction._0);
            }, 2001);

            currentState.directlineInstance?.postSenderAction(SenderActionAction._1);
          } else {
            currentState.directlineInstance?.postSenderAction(SenderActionAction._0);
          }
        }

        if (action.type === 'WEB_CHAT/SUBMIT_SEND_BOX') { // send cancel typing. this has no effect if already is off because of distinctUntilChanged in directlinejs
          currentState.directlineInstance?.postSenderAction(SenderActionAction._0);
        }

        if (action.type === 'WEB_CHAT/STOP_DICTATE') {
          if (currentBFState.sendBoxValue) { // fixes microphone stays disabled when speaking is on and user inputs text in sendbox   (stopping)
            bfDispatch({ type: 'WEB_CHAT/SET_DICTATE_STATE', payload: { dictateState: 0 } }); // force dictate state to IDLE instead of STOPPING
          }
        }

        return next(action);
      };
      const conversationHistoryActivities = getState().mergedChatOptions?.conversationHistoryActivities || [];
      const bfStore = createStoreWithDevTools({activities: conversationHistoryActivities?.map((ch, idx) => mapConversationHistoryActivityToActivity(ch, conversationHistoryActivities?.length - 11 <= idx)) || [] }, ...[coreDruidMiddleware, customDruidMiddleware]);


      return { bfStore, subscriptions };
    },
    [druidWebChatDispatch, initialOptions.webChatEventHandler, activityFromBot] /* , druidWebChatState?.mergedChatOptions?.conversationHistoryActivities */
  );
};

